import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Save, X, MapPin } from "lucide-react";
import AdminShell from "@/components/AdminShell";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
    slug: "",
    title: "",
    type: "Full-time",
    location: "Remote",
    summary: "",
    responsibilities_text: "",
    screening_questions_text: "",
    active: true,
};

function toLines(arr) {
    return (arr || []).join("\n");
}
function fromLines(text) {
    return (text || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
}

function JobForm({ initial, onSave, onCancel, isNew }) {
    const [form, setForm] = useState(initial);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((s) => ({ ...s, [k]: v }));
    };

    const save = async (e) => {
        e?.preventDefault?.();
        if (!form.slug || !form.title) return toast.error("Slug and title are required");
        setBusy(true);
        try {
            const payload = {
                slug: form.slug,
                title: form.title,
                type: form.type,
                location: form.location,
                summary: form.summary,
                responsibilities: fromLines(form.responsibilities_text),
                screening_questions: fromLines(form.screening_questions_text),
                active: form.active,
            };
            if (isNew) {
                await api.post("/admin/jobs", payload);
            } else {
                const { slug, ...rest } = payload;
                await api.patch(`/admin/jobs/${initial.slug}`, rest);
            }
            toast.success("Saved");
            onSave();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={save} className="card-surface p-6 lg:p-8 space-y-6" data-testid="job-form">
            <div className="flex items-start justify-between">
                <div>
                    <div className="eyebrow text-saffron-brand mb-1">{isNew ? "New role" : "Edit role"}</div>
                    <div className="font-display text-xl">Careers posting</div>
                </div>
                <button type="button" onClick={onCancel} className="text-white/55 hover:text-white">
                    <X size={18} />
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <div>
                    <span className="field-label">Slug (URL ID, no spaces)</span>
                    <input className="field-input" value={form.slug} onChange={set("slug")} disabled={!isNew} placeholder="brand-strategy-intern" data-testid="job-input-slug" />
                </div>
                <div>
                    <span className="field-label">Title</span>
                    <input className="field-input" value={form.title} onChange={set("title")} placeholder="Brand Strategy Intern" data-testid="job-input-title" />
                </div>
                <div>
                    <span className="field-label">Type</span>
                    <input className="field-input" value={form.type} onChange={set("type")} placeholder="Full-time / Internship / Contract" data-testid="job-input-type" />
                </div>
                <div>
                    <span className="field-label">Location</span>
                    <input className="field-input" value={form.location} onChange={set("location")} placeholder="Mumbai · Hybrid" data-testid="job-input-location" />
                </div>
                <div className="md:col-span-2">
                    <span className="field-label">Summary</span>
                    <textarea rows={2} className="field-input" value={form.summary} onChange={set("summary")} data-testid="job-input-summary" />
                </div>
                <div className="md:col-span-2">
                    <span className="field-label">Responsibilities (one per line)</span>
                    <textarea rows={4} className="field-input" value={form.responsibilities_text} onChange={set("responsibilities_text")} placeholder="Draft strategic briefs and positioning statements&#10;Conduct primary brand research" data-testid="job-input-responsibilities" />
                </div>
                <div className="md:col-span-2">
                    <span className="field-label">Screening questions (one per line)</span>
                    <textarea rows={4} className="field-input" value={form.screening_questions_text} onChange={set("screening_questions_text")} data-testid="job-input-screening" />
                </div>
                <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer mt-2 md:col-span-2">
                    <input type="checkbox" checked={form.active} onChange={set("active")} className="accent-saffron" data-testid="job-input-active" />
                    Active (visible on /careers)
                </label>
            </div>
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/8">
                <button type="button" onClick={onCancel} className="btn-secondary text-xs">Cancel</button>
                <button type="submit" disabled={busy} className="btn-primary text-xs" data-testid="job-save-btn">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {isNew ? "Publish role" : "Save changes"}
                </button>
            </div>
        </form>
    );
}

export default function AdminJobs() {
    const [rows, setRows] = useState(null);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        const { data } = await api.get("/admin/jobs");
        setRows(data);
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = async (slug) => {
        if (!window.confirm(`Delete '${slug}'? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/jobs/${slug}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    return (
        <AdminShell title="Careers · Job Postings">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <p className="text-white/55 text-sm max-w-2xl">
                    Manage open roles. Active roles show up at /careers and accept applications. Hidden roles still keep their candidate history.
                </p>
                {!creating && !editing && (
                    <button onClick={() => setCreating(true)} className="btn-primary text-xs" data-testid="job-add-btn">
                        <Plus size={14} /> Post new role
                    </button>
                )}
            </div>

            {creating && (
                <div className="mb-8">
                    <JobForm isNew initial={EMPTY} onCancel={() => setCreating(false)} onSave={() => { setCreating(false); load(); }} />
                </div>
            )}

            {editing && (
                <div className="mb-8">
                    <JobForm
                        initial={{
                            ...editing,
                            responsibilities_text: toLines(editing.responsibilities),
                            screening_questions_text: toLines(editing.screening_questions),
                        }}
                        onCancel={() => setEditing(null)}
                        onSave={() => { setEditing(null); load(); }}
                    />
                </div>
            )}

            {rows === null ? (
                <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-saffron" size={20} /></div>
            ) : rows.length === 0 ? (
                <div className="card-surface p-12 text-center">
                    <div className="eyebrow mb-3">Nothing here yet</div>
                    <h3 className="font-display text-xl mb-4">Post your first role</h3>
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => (
                        <div key={r.slug} className="card-surface p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" data-testid={`job-row-${r.slug}`}>
                            <div className="md:col-span-4">
                                <div className="eyebrow mb-1">{r.slug}</div>
                                <div className="font-display text-base text-white">{r.title}</div>
                            </div>
                            <div className="md:col-span-2">
                                <span className="tag">{r.type}</span>
                            </div>
                            <div className="md:col-span-3 text-white/65 text-sm flex items-center gap-1">
                                <MapPin size={11} /> {r.location}
                            </div>
                            <div className="md:col-span-1">
                                <span className={`tag ${r.active ? "is-active" : ""}`}>{r.active ? "Live" : "Hidden"}</span>
                            </div>
                            <div className="md:col-span-2 flex md:justify-end gap-2">
                                <button onClick={() => setEditing(r)} className="btn-secondary text-xs" data-testid={`job-edit-${r.slug}`}>Edit</button>
                                <button onClick={() => remove(r.slug)} className="btn-secondary text-xs hover:!border-red-400 hover:!text-red-400" data-testid={`job-delete-${r.slug}`}>
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AdminShell>
    );
}
